<script lang="ts">
    import { VpPlayIcon } from '@decathlon-experience/icons/svelte';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';
    import Loader from '$saga/components/Loader.svelte';
    import { TOUR_TYPE_GIR, TYPEBOOKING_EBOOKING } from '$saga/constants/tour';
    import { datesInfos } from '$saga/views/Tour/store';
    import type { AgeRules, FormValues } from '$saga/views/Tour/types';

    import AdultsInput from './AdultsInput.svelte';
    import ChildrenInput from './ChildrenInput.svelte';
    import TravellerAgeInputs from './TravellerAgeInputs.svelte';
    import {
        areAgesValid,
        computeIsChildrenAllowed,
        computeIsPaxValid,
        computeMinTotalPax,
        computePaxLimits,
        hasAgeBelowMinimum,
        hasAgeOutsideBounds,
    } from './utils';

    export let tourType: number;
    export let bookingType: string;
    export let isTravelAloneAuthorized: boolean;
    export let minAgeAllowed: number | null;
    export let maxAgeAllowed: number | null;
    export let formValues: FormValues;
    export let ageRules: AgeRules;
    export let showIndividualAges: boolean;
    export let showAdultAges: boolean;
    export let showChildAges: boolean;
    export let adultMinAge: number;
    export let adultMaxAge: number | null;
    export let childMinAge: number;
    export let childMaxAge: number | null;
    export let hideStock: boolean = false;
    export let requiredPax: number | null = null;
    export let isLoading: boolean = false;

    const isEbookingType = bookingType === TYPEBOOKING_EBOOKING;
    const isGir = tourType === TOUR_TYPE_GIR;
    const isChildrenAllowed = computeIsChildrenAllowed(
        isEbookingType,
        minAgeAllowed,
        maxAgeAllowed,
    );
    const minTotalPax = computeMinTotalPax(isTravelAloneAuthorized);

    $: ({
        minAdults: minAdultsRuleValue,
        maxAdults: maxAdultsRuleValue,
        minChildren: minChildrenRuleValue,
        maxChildren: maxChildrenRuleValue,
    } = computePaxLimits({
        isEbookingType,
        isGir,
        isChildrenAllowed,
        isTravelAloneAuthorized,
        ageRules,
        adults: formValues.adults,
        children: formValues.children,
        selectedDate: formValues.selectedDate,
        remainingStock: $datesInfos?.remainingStock ?? null,
    }));

    $: areAdultsAgeValid =
        !showAdultAges ||
        areAgesValid(
            formValues.adultsAges,
            formValues.adults,
            adultMinAge,
            adultMaxAge,
        );
    $: areChildrenAgeValid =
        !showChildAges ||
        areAgesValid(
            formValues.childrenAges,
            formValues.children,
            childMinAge,
            childMaxAge,
        );
    $: adultAgeBelowMinimum =
        showAdultAges &&
        hasAgeBelowMinimum(
            formValues.adultsAges,
            formValues.adults,
            adultMinAge,
        );
    $: ageOutsideBounds =
        showAdultAges &&
        !adultAgeBelowMinimum &&
        hasAgeOutsideBounds(
            formValues.adultsAges,
            formValues.adults,
            adultMinAge,
            adultMaxAge,
        );

    $: individualAgesValid =
        !showIndividualAges || (areAdultsAgeValid && areChildrenAgeValid);
    $: snowtrexPaxMismatch =
        requiredPax !== null &&
        formValues.adults + formValues.children !== requiredPax;

    let isValid = false;
    $: {
        if (individualAgesValid) {
            isValid = computeIsPaxValid({
                adults: formValues.adults,
                children: formValues.children,
                minAdults: minAdultsRuleValue,
                maxAdults: maxAdultsRuleValue,
                minChildren: minChildrenRuleValue,
                maxChildren: maxChildrenRuleValue,
                minTotalPax,
                requiredPax,
            });
        } else {
            isValid = false;
        }
    }
</script>

<div class="mx-xl h-full flex flex-col gap-xl py-2xl">
    <!-- Existing adult/child selector content here -->
    <AdultsInput
        {minAgeAllowed}
        {maxAgeAllowed}
        {tourType}
        {bookingType}
        bind:formValues
        {ageRules}
        maxRuleValue={maxAdultsRuleValue}
        minRuleValue={minAdultsRuleValue}
        dateStatusName={$datesInfos?.statusName ?? ''}
    />
    {#if isEbookingType && isChildrenAllowed}
        <ChildrenInput
            {minAgeAllowed}
            {maxAgeAllowed}
            bind:formValues
            {ageRules}
            maxRuleValue={maxChildrenRuleValue}
            minRuleValue={minChildrenRuleValue}
        />
    {/if}
    {#if showIndividualAges}
        <TravellerAgeInputs
            bind:adultsAges={formValues.adultsAges}
            bind:childrenAges={formValues.childrenAges}
            adultCount={formValues.adults}
            childCount={formValues.children}
            {showAdultAges}
            {showChildAges}
            {adultMinAge}
            {childMinAge}
            {childMaxAge}
        />
    {/if}
    {#if snowtrexPaxMismatch}
        <div class="text-sm text-status-negative">
            {trans('tour.aside_panel.pax_popover.snowtrex_pax_unavailable')}
        </div>
    {:else if ageOutsideBounds}
        <div class="text-sm text-status-negative">
            {trans('tour.aside_panel.pax_popover.age_unavailable')}
        </div>
    {/if}
    {#if !hideStock && $datesInfos?.remainingStock}
        <div class="text-center">
            <!--  eslint-disable-next-line svelte/no-at-html-tags -->
            {@html trans('tour.aside_panel.travelgate.remaining', {
                '%count%': $datesInfos.remainingStock,
            })}
        </div>
    {/if}

    {#if minTotalPax >= 2 && !isTravelAloneAuthorized && formValues.adults + formValues.children < minTotalPax}
        <div class="text-sm text-status-negative">
            {trans('tour.travel_alone.not_allowed')}
        </div>
    {/if}
</div>

<!-- Footer -->
<div
    class="sticky bottom-0 left-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
>
    <Button
        type="submit"
        form="prefunnel"
        class={cn('w-full', !isValid && 'cursor-not-allowed opacity-[0.38]')}
        disabled={!isValid || isLoading}
        endIcon={!isLoading ? VpPlayIcon : undefined}
    >
        {#if isLoading}
            <Loader />
        {:else}
            {trans('common.continue')}
        {/if}
    </Button>
</div>
